import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useProject, useProjectBOM, useLinkProjectBOM, useUpdateProject, useUpdatePhase, useCreatePhase, useReorderPhases } from '@/hooks/useProject';
import { useTasksByProject } from '@/hooks/useTasks';
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_COLORS,
  PROJECT_TYPE_LABELS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  PHASE_STATUS_LABELS,
  PHASE_STATUS_COLORS,
  PHASE_TYPE_LABELS,
  PHASE_TYPE_COLORS,
  IProjectPhase,
  IProjectUpdateRequest,
  IPhaseUpdateRequest,
  ProjectStatus,
  PhaseStatus,
  PhaseType,
  Priority,
  IPhaseCreateRequest,
} from '@/types/project';
import { ITask } from '@/types/task';
import PhaseTimeline from './PhaseTimeline';
import PhaseTimelinePlanner from './PhaseTimelinePlanner';
import PhaseDetailPanel from './PhaseDetailPanel';
import ProjectTaskModal from './ProjectTaskModal';
import PhaseFlowView from './PhaseFlowView';
import ActivityLog from './ActivityLog';
import { logger } from '@/utils/logger';

// Phase type options for the dropdown
const PHASE_TYPE_OPTIONS: { value: PhaseType; label: string }[] = [
  { value: 'concept', label: 'Concept' },
  { value: 'feasibility', label: 'Feasibility' },
  { value: 'pre_bom', label: 'PRE-BOM' },
  { value: 'evt', label: 'EVT' },
  { value: 'dvt', label: 'DVT' },
  { value: 'pvt', label: 'PVT' },
  { value: 'pp', label: 'PP' },
  { value: 'mp', label: 'MP' },
  { value: 'sustaining', label: 'Sustaining' },
  { value: 'architecture', label: 'Architecture' },
  { value: 'sprint_planning', label: 'Sprint Planning' },
  { value: 'development', label: 'Development' },
  { value: 'testing', label: 'Testing/QA' },
  { value: 'staging', label: 'Staging' },
  { value: 'release', label: 'Release' },
  { value: 'maintenance', label: 'Maintenance' },
];

interface ProjectDetailProps {
  projectId: string;
  onBack?: () => void;
}

const ProjectDetail: React.FC<ProjectDetailProps> = ({ projectId, onBack: _onBack }) => {
  const { t } = useTranslation('project');
  const { data, isLoading, error, refetch } = useProject(projectId);
  const { data: bomData, refetch: refetchBOM } = useProjectBOM(projectId);
  const { data: tasksData, refetch: refetchTasks } = useTasksByProject(projectId);
  const updateProjectMutation = useUpdateProject();
  const updatePhaseMutation = useUpdatePhase();
  const createPhaseMutation = useCreatePhase();
  const reorderPhasesMutation = useReorderPhases();
  // These will be used in future implementations for product create/link dialogs
  void useLinkProjectBOM; // Will be used for link product mutation
  const [, setShowCreateProductDialog] = useState(false);
  const [, setShowLinkProductDialog] = useState(false);
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());

  // Edit Project Dialog state
  const [showEditProjectDialog, setShowEditProjectDialog] = useState(false);
  const [editProject, setEditProject] = useState<Partial<IProjectUpdateRequest>>({});

  // Add Phase Dialog state
  const [showAddPhaseDialog, setShowAddPhaseDialog] = useState(false);
  const [newPhase, setNewPhase] = useState<Partial<IPhaseCreateRequest>>({
    phase_name: '',
    phase_type: 'concept',
    planned_start: '',
    planned_end: '',
    owner: '',
  });

  // Task Modal state (unified task system)
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedPhaseForTask, setSelectedPhaseForTask] = useState<{ id: string; name: string } | null>(null);
  const [editingTask, setEditingTask] = useState<ITask | null>(null);

  // Get all tasks from unified task system
  const allTasks = useMemo(() => tasksData?.tasks || [], [tasksData]);

  // Group tasks by phase
  const tasksByPhase = useMemo(() => {
    const grouped: Record<string, ITask[]> = {};
    allTasks.forEach((task) => {
      const phaseId = task.phase_id || 'unassigned';
      if (!grouped[phaseId]) {
        grouped[phaseId] = [];
      }
      grouped[phaseId].push(task);
    });
    // Sort tasks within each phase by sort_order
    Object.keys(grouped).forEach((phaseId) => {
      grouped[phaseId].sort((a, b) => a.sort_order - b.sort_order);
    });
    return grouped;
  }, [allTasks]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Find current phase (first in_progress or review phase, or first pending if none)
  const currentPhase = useMemo(() => {
    if (!data?.phases || data.phases.length === 0) return undefined;
    const sortedPhases = [...data.phases].sort((a, b) => a.sort_order - b.sort_order);

    // First, look for an in_progress or review phase
    const activePhase = sortedPhases.find(
      (p) => p.status === 'in_progress' || p.status === 'review'
    );
    if (activePhase) return activePhase;

    // If no active phase, find first not_started phase
    const notStartedPhase = sortedPhases.find((p) => p.status === 'not_started');
    if (notStartedPhase) return notStartedPhase;

    // If all phases are completed, return the last one
    return sortedPhases[sortedPhases.length - 1];
  }, [data?.phases]);

  const handleCreateProduct = () => {
    setShowCreateProductDialog(true);
  };

  const handleLinkProduct = () => {
    setShowLinkProductDialog(true);
  };

  const handleRefresh = () => {
    refetch();
    refetchBOM();
    refetchTasks();
  };

  const handlePhaseClick = (phase: IProjectPhase) => {
    // In a future enhancement, this could navigate to a phase-specific view
    // or expand the phase details in the current view
    logger.log('Phase clicked:', phase.phase_name);
  };

  const handlePhaseStatusChange = (phaseId: string, newStatus: PhaseStatus) => {
    updatePhaseMutation.mutate(
      { projectId, phaseId, data: { status: newStatus } },
      {
        onSuccess: () => {
          setEditingPhaseId(null);
          refetch();
        },
      }
    );
  };

  const handleAddPhase = () => {
    if (!newPhase.phase_name || !newPhase.phase_type || !newPhase.planned_start || !newPhase.planned_end || !newPhase.owner) {
      return;
    }
    createPhaseMutation.mutate(
      { projectId, data: newPhase as IPhaseCreateRequest },
      {
        onSuccess: () => {
          setShowAddPhaseDialog(false);
          setNewPhase({
            phase_name: '',
            phase_type: 'concept',
            planned_start: '',
            planned_end: '',
            owner: '',
          });
          refetch();
        },
      }
    );
  };

  // Open task modal for a specific phase
  const handleAddTaskForPhase = (phase: IProjectPhase) => {
    setSelectedPhaseForTask({ id: phase.id, name: phase.phase_name });
    setEditingTask(null);
    setShowTaskModal(true);
  };

  // Open task modal for editing
  const handleEditTask = (task: ITask, phase: IProjectPhase) => {
    setSelectedPhaseForTask({ id: phase.id, name: phase.phase_name });
    setEditingTask(task);
    setShowTaskModal(true);
  };

  // Toggle phase expansion
  const togglePhaseExpand = (phaseId: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) {
        next.delete(phaseId);
      } else {
        next.add(phaseId);
      }
      return next;
    });
  };

  // Close task modal
  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setSelectedPhaseForTask(null);
    setEditingTask(null);
    setPriorDepTask(null);
    setPostDepTask(null);
    refetchTasks();
  };

  // State for creating tasks with pre-set dependencies
  const [priorDepTask, setPriorDepTask] = useState<ITask | null>(null);
  const [postDepTask, setPostDepTask] = useState<ITask | null>(null);

  // Add task as a prior dependency (new task will be a prior to the clicked task)
  const handleAddPriorTask = (task: ITask) => {
    const phase = data?.phases?.find(p => p.id === task.phase_id);
    if (phase) {
      setSelectedPhaseForTask({ id: phase.id, name: phase.phase_name });
      setEditingTask(null);
      setPostDepTask(task); // The new task will have the clicked task as its post dependency
      setPriorDepTask(null);
      setShowTaskModal(true);
    }
  };

  // Add task as a post dependency (new task will depend on the clicked task)
  const handleAddPostTask = (task: ITask) => {
    const phase = data?.phases?.find(p => p.id === task.phase_id);
    if (phase) {
      setSelectedPhaseForTask({ id: phase.id, name: phase.phase_name });
      setEditingTask(null);
      setPriorDepTask(task); // The new task will have the clicked task as its prior dependency
      setPostDepTask(null);
      setShowTaskModal(true);
    }
  };

  // Open edit project dialog
  const handleEditProject = () => {
    if (!data?.project) return;
    const p = data.project;
    setEditProject({
      project_name: p.project_name,
      status: p.status,
      priority: p.priority,
      project_manager: p.project_manager,
      start_date: p.start_date,
      target_end_date: p.target_end_date,
      budget: p.budget,
      currency: p.currency,
      description: p.description,
    });
    setShowEditProjectDialog(true);
  };

  // Save project changes
  const handleSaveProject = () => {
    updateProjectMutation.mutate(
      { id: projectId, data: editProject },
      {
        onSuccess: () => {
          setShowEditProjectDialog(false);
          refetch();
        },
      }
    );
  };

  // Handle phase drag-and-drop reordering
  const handlePhaseDragEnd = (result: DropResult) => {
    if (!result.destination || !data?.phases) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    // Get sorted phases
    const sortedPhases = [...data.phases].sort((a, b) => a.sort_order - b.sort_order);

    // Reorder the array
    const [movedPhase] = sortedPhases.splice(sourceIndex, 1);
    sortedPhases.splice(destIndex, 0, movedPhase);

    // Create new sort orders
    const orders = sortedPhases.map((phase, index) => ({
      id: phase.id,
      sort_order: index + 1,
    }));

    reorderPhasesMutation.mutate(
      { projectId, data: { orders } },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <p className="text-lg">{t('common.loadError')}</p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  const { project, phases: rawPhases } = data;

  // Ensure phases is an array (API may return null)
  const phases = rawPhases ?? [];

  // Use tasks from the unified task system
  const tasks = allTasks;

  // Calculate overall progress (for reference, actual progress comes from server)
  const completedPhases = phases.filter((p) => p.status === 'completed').length;
  void completedPhases; // Progress is displayed via project.progress from the server

  return (
    <div className="space-y-6">
      {/* Project Overview */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Project Info Card */}
        <div className="md:col-span-2 bg-gray-50 dark:bg-gray-900 rounded-lg p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {project.project_name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{project.project_code}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${PROJECT_STATUS_COLORS[project.status]}`}>
                {PROJECT_STATUS_LABELS[project.status]}
              </span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${PRIORITY_COLORS[project.priority]}`}>
                {PRIORITY_LABELS[project.priority]}
              </span>
              <button
                onClick={handleEditProject}
                className="p-2 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={t('detail.edit')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 mb-4">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('detail.type')}</span>
              <p className="font-medium text-gray-900 dark:text-white">{PROJECT_TYPE_LABELS[project.project_type]}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('detail.manager')}</span>
              <p className="font-medium text-gray-900 dark:text-white">{project.project_manager}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('detail.startDate')}</span>
              <p className="font-medium text-gray-900 dark:text-white">{formatDate(project.start_date)}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('detail.targetEndDate')}</span>
              <p className="font-medium text-gray-900 dark:text-white">{formatDate(project.target_end_date)}</p>
            </div>
            {project.budget && (
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('detail.budget')}</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {project.budget.toLocaleString()} {project.currency}
                </p>
              </div>
            )}
            {project.customer_code && (
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('detail.customer')}</span>
                <p className="font-medium text-gray-900 dark:text-white">{project.customer_code}</p>
              </div>
            )}
          </div>

          {project.description && (
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('detail.description')}</span>
              <p className="text-gray-700 dark:text-gray-300 mt-1">{project.description}</p>
            </div>
          )}
        </div>

        {/* Progress Card */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('detail.progress')}</h3>

          <div className="text-center mb-6">
            <div className="relative inline-flex items-center justify-center w-32 h-32">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  className="text-gray-200 dark:text-gray-700"
                  strokeWidth="8"
                  stroke="currentColor"
                  fill="transparent"
                  r="56"
                  cx="64"
                  cy="64"
                />
                <circle
                  className="text-emerald-500"
                  strokeWidth="8"
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="transparent"
                  r="56"
                  cx="64"
                  cy="64"
                  style={{
                    strokeDasharray: 352,
                    strokeDashoffset: 352 - (352 * project.progress) / 100,
                  }}
                />
              </svg>
              <span className="absolute text-2xl font-bold text-gray-900 dark:text-white">
                {project.progress}%
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('detail.phases')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{phases.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('detail.tasks')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{tasks.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('detail.completedPhases')}</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {completedPhases} / {phases.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Phase Timeline */}
      {phases.length > 0 && (
        <PhaseTimeline
          phases={phases}
          currentPhaseId={currentPhase?.id}
          onPhaseClick={handlePhaseClick}
        />
      )}

      {/* Phase-Specific Actions */}
      <PhaseDetailPanel
        project={project}
        currentPhase={currentPhase}
        linkedProduct={bomData?.product}
        onCreateProduct={handleCreateProduct}
        onLinkProduct={handleLinkProduct}
        onRefresh={handleRefresh}
      />

      {/* Phase Timeline Planner */}
      {phases.length > 0 && (
        <PhaseTimelinePlanner
          phases={phases}
          projectStartDate={project.start_date}
          projectEndDate={project.target_end_date}
          onUpdatePhase={(phaseId: string, data: IPhaseUpdateRequest) => {
            updatePhaseMutation.mutate(
              { projectId, phaseId, data },
              { onSuccess: () => refetch() }
            );
          }}
          isUpdating={updatePhaseMutation.isLoading}
        />
      )}

      {/* Phases Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('phases.title')}</h3>
          <button
            onClick={() => setShowAddPhaseDialog(true)}
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            {t('phases.add')}
          </button>
        </div>

        {phases.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>{t('phases.noPhases')}</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handlePhaseDragEnd}>
            <Droppable droppableId="phases">
              {(provided) => (
                <div
                  className="space-y-3"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {phases.sort((a, b) => a.sort_order - b.sort_order).map((phase, index) => {
                    const phaseTasks = tasksByPhase[phase.id] || [];
                    const completedTasks = phaseTasks.filter((t) => t.status === 'done').length;
                    const phaseProgress = phaseTasks.length > 0 ? Math.round((completedTasks / phaseTasks.length) * 100) : 0;
                    const isExpanded = expandedPhases.has(phase.id);
                    const phaseColors = PHASE_TYPE_COLORS[phase.phase_type] || PHASE_TYPE_COLORS.custom;

                    return (
                      <Draggable key={phase.id} draggableId={phase.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${phaseColors.bg} ${phaseColors.border} border rounded-lg p-4 ${
                              snapshot.isDragging ? 'shadow-lg ring-2 ring-emerald-500' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                {/* Drag handle */}
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  title={t('phases.dragToReorder')}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                  </svg>
                                </div>
                                {/* Expand/collapse button */}
                                <button
                                  onClick={() => togglePhaseExpand(phase.id)}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                  title={isExpanded ? t('phases.collapse', 'Collapse') : t('phases.expand', 'Expand')}
                                >
                                  <svg
                                    className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                                <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                  {PHASE_TYPE_LABELS[phase.phase_type]}
                                </span>
                                <h4 className="font-medium text-gray-900 dark:text-white">{phase.phase_name}</h4>
                              </div>
                              <div className="flex items-center gap-2">
                                {editingPhaseId === phase.id ? (
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={phase.status}
                                      onChange={(e) => handlePhaseStatusChange(phase.id, e.target.value as PhaseStatus)}
                                      className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                      disabled={updatePhaseMutation.isLoading}
                                    >
                                      <option value="not_started">{PHASE_STATUS_LABELS.not_started}</option>
                                      <option value="in_progress">{PHASE_STATUS_LABELS.in_progress}</option>
                                      <option value="review">{PHASE_STATUS_LABELS.review}</option>
                                      <option value="completed">{PHASE_STATUS_LABELS.completed}</option>
                                    </select>
                                    <button
                                      onClick={() => setEditingPhaseId(null)}
                                      className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                      title={t('common.cancel')}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${PHASE_STATUS_COLORS[phase.status]}`}>
                                      {PHASE_STATUS_LABELS[phase.status]}
                                    </span>
                                    <button
                                      onClick={() => setEditingPhaseId(phase.id)}
                                      className="p-1 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                                      title={t('phases.editStatus')}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400 mb-3">
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {phase.owner}
                              </span>
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatDate(phase.planned_start)} - {formatDate(phase.planned_end)}
                              </span>
                              <button
                                onClick={() => togglePhaseExpand(phase.id)}
                                className="flex items-center hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                {completedTasks} / {phaseTasks.length} {t('tasks.title').toLowerCase()}
                              </button>
                            </div>

                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
                              <div
                                className="h-full bg-emerald-500 rounded-full transition-all"
                                style={{ width: `${phaseProgress}%` }}
                              />
                            </div>

                            {/* Phase Flow View (expanded) - React Flow visualization */}
                            {isExpanded && (
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <PhaseFlowView
                                  tasks={phaseTasks}
                                  onEditTask={(task) => handleEditTask(task, phase)}
                                  onAddPriorTask={handleAddPriorTask}
                                  onAddPostTask={handleAddPostTask}
                                  onAddTask={() => handleAddTaskForPhase(phase)}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Add Phase Dialog */}
      {showAddPhaseDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('phases.add')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('phases.form.name')}
                </label>
                <input
                  type="text"
                  value={newPhase.phase_name || ''}
                  onChange={(e) => setNewPhase({ ...newPhase, phase_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder={t('phases.form.namePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('phases.form.type')}
                </label>
                <select
                  value={newPhase.phase_type || 'concept'}
                  onChange={(e) => setNewPhase({ ...newPhase, phase_type: e.target.value as PhaseType })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                >
                  {PHASE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('phases.form.owner')}
                </label>
                <input
                  type="text"
                  value={newPhase.owner || ''}
                  onChange={(e) => setNewPhase({ ...newPhase, owner: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder={t('phases.form.ownerPlaceholder')}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('phases.form.startDate')}
                  </label>
                  <input
                    type="date"
                    value={newPhase.planned_start || ''}
                    onChange={(e) => setNewPhase({ ...newPhase, planned_start: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('phases.form.endDate')}
                  </label>
                  <input
                    type="date"
                    value={newPhase.planned_end || ''}
                    onChange={(e) => setNewPhase({ ...newPhase, planned_end: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddPhaseDialog(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAddPhase}
                disabled={createPhaseMutation.isLoading || !newPhase.phase_name || !newPhase.owner || !newPhase.planned_start || !newPhase.planned_end}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createPhaseMutation.isLoading ? t('common.saving') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Task Modal (unified task system) */}
      {showTaskModal && selectedPhaseForTask && (
        <ProjectTaskModal
          isOpen={showTaskModal}
          onClose={handleCloseTaskModal}
          task={editingTask}
          allTasks={allTasks}
          projectId={projectId}
          projectName={project.project_name}
          phaseId={selectedPhaseForTask.id}
          phaseName={selectedPhaseForTask.name}
          initialPriorDep={priorDepTask}
          initialPostDep={postDepTask}
        />
      )}

      {/* Activity Log */}
      {allTasks.length > 0 && (
        <ActivityLog tasks={allTasks} />
      )}

      {/* Edit Project Dialog */}
      {showEditProjectDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('detail.editProject')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.projectName')}
                </label>
                <input
                  type="text"
                  value={editProject.project_name || ''}
                  onChange={(e) => setEditProject({ ...editProject, project_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('detail.status')}
                  </label>
                  <select
                    value={editProject.status || 'planning'}
                    onChange={(e) => setEditProject({ ...editProject, status: e.target.value as ProjectStatus })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  >
                    {Object.entries(PROJECT_STATUS_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.priority')}
                  </label>
                  <select
                    value={editProject.priority || 'medium'}
                    onChange={(e) => setEditProject({ ...editProject, priority: e.target.value as Priority })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  >
                    {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.projectManager')}
                </label>
                <input
                  type="text"
                  value={editProject.project_manager || ''}
                  onChange={(e) => setEditProject({ ...editProject, project_manager: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.startDate')}
                  </label>
                  <input
                    type="date"
                    value={editProject.start_date || ''}
                    onChange={(e) => setEditProject({ ...editProject, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.targetEndDate')}
                  </label>
                  <input
                    type="date"
                    value={editProject.target_end_date || ''}
                    onChange={(e) => setEditProject({ ...editProject, target_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.budget')}
                  </label>
                  <input
                    type="number"
                    value={editProject.budget || ''}
                    onChange={(e) => setEditProject({ ...editProject, budget: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('detail.currency')}
                  </label>
                  <select
                    value={editProject.currency || 'TWD'}
                    onChange={(e) => setEditProject({ ...editProject, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="TWD">TWD</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.description')}
                </label>
                <textarea
                  value={editProject.description || ''}
                  onChange={(e) => setEditProject({ ...editProject, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditProjectDialog(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveProject}
                disabled={updateProjectMutation.isLoading}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateProjectMutation.isLoading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProjectDetail;
